// Robust Text-to-Speech layer with MP3 FALLBACK for Google TV / Android TV.
//
// Strategy:
//   1. PREFER browser SpeechSynthesis when supported AND a usable voice is loaded
//      (faster, lower latency, better quality on devices that have native voices).
//   2. FALL BACK to pre-generated MP3 clips served from /audio/*.mp3 when:
//        - SpeechSynthesis is missing (Google TV WebView, old Smart TV browsers),
//        - voices fail to load within 1.5s, or
//        - SpeechSynthesis.speak() never fires `onstart` (silent failure).
//      MP3 fallback uses HTML5 <audio> which works on EVERY Android TV browser.
//
// MP3 clips were pre-fetched from Google's TTS at build time and bundled at
// /app/frontend/public/audio/  →  /audio/* in production.
//   intro.mp3   "Token number"
//   0.mp3 … 9.mp3   digits 0-9
//   outro.mp3   "please proceed to the counter"
// For token "25" we play: intro + 2.mp3 + 5.mp3 + outro.

let _primed = false;
let _voices = [];
let _voicesLoaded = false;
let _keepAliveInterval = null;
let _lastError = null;
let _speechWorks = null; // tri-state: null=unknown, true=verified, false=verified-fail
let _audioUnlocked = false;
let _preferredLang = "en-US";

const AUDIO_BASE = "/audio";

// ---------------- Web Speech API ----------------

export function isTTSSupported() {
  return (
    typeof window !== "undefined" &&
    "speechSynthesis" in window &&
    "SpeechSynthesisUtterance" in window
  );
}

function loadVoices() {
  if (!isTTSSupported()) return;
  const v = window.speechSynthesis.getVoices();
  if (v && v.length) {
    _voices = v;
    _voicesLoaded = true;
  }
}

function pickVoice() {
  if (!_voicesLoaded) loadVoices();
  if (!_voices.length) return null;
  const prefs = [
    (v) => v.lang === "en-IN",
    (v) => v.lang === "en-GB",
    (v) => v.lang === "en-US",
    (v) => v.lang?.startsWith("en"),
    () => true,
  ];
  for (const p of prefs) {
    const m = _voices.find(p);
    if (m) return m;
  }
  return null;
}

// ---------------- MP3 Fallback ----------------

// Caches a single Audio instance per clip so we don't refetch on every play.
const _audioCache = new Map();
function getAudio(name) {
  let a = _audioCache.get(name);
  if (!a) {
    a = new Audio(`${AUDIO_BASE}/${name}`);
    a.preload = "auto";
    a.volume = 1.0;
    _audioCache.set(name, a);
  }
  return a;
}

// Preload all clips in parallel (best-effort).
function preloadClips() {
  ["intro.mp3", "outro.mp3", ...Array.from({ length: 10 }, (_, i) => `${i}.mp3`)].forEach(
    (n) => {
      try { getAudio(n); } catch {/* ignore */}
    }
  );
}

// Play one Audio sequentially. Returns a promise that resolves on `ended`,
// rejects on `error`, or times out after 4s.
function playOne(name) {
  return new Promise((resolve, reject) => {
    const a = getAudio(name);
    try { a.currentTime = 0; } catch {/* some browsers throw before metadata */}
    let settled = false;
    const cleanup = () => {
      a.removeEventListener("ended", onEnd);
      a.removeEventListener("error", onErr);
    };
    const onEnd = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };
    const onErr = (e) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(e);
    };
    a.addEventListener("ended", onEnd);
    a.addEventListener("error", onErr);
    // Safety timeout — never block forever.
    setTimeout(() => {
      if (!settled) {
        settled = true;
        cleanup();
        resolve();
      }
    }, 4000);
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch((err) => {
        if (!settled) {
          settled = true;
          cleanup();
          reject(err);
        }
      });
    }
  });
}

async function playMp3Announcement(tokenNumber) {
  const tok = String(tokenNumber || "");
  if (!tok) return false;
  try {
    await playOne("intro.mp3");
    for (const ch of tok) {
      if (ch >= "0" && ch <= "9") {
        await playOne(`${ch}.mp3`);
      }
    }
    await playOne("outro.mp3");
    return true;
  } catch (err) {
    _lastError = `mp3 fallback failed: ${err?.message || err}`;
    return false;
  }
}

// ---------------- Public API ----------------

// MUST be called from inside a user gesture handler (onClick).
// Unlocks BOTH speech synthesis AND HTML5 audio playback on the page so
// subsequent automatic announcements aren't blocked by autoplay policies.
export function primeTTS() {
  preloadClips();

  // Unlock HTML5 Audio with a 1-sample silent play. This is the magic that
  // makes Google TV / iOS Safari trust us with audio for the rest of the session.
  try {
    const silent = getAudio("0.mp3");
    silent.volume = 0;
    const p = silent.play();
    if (p && typeof p.then === "function") {
      p.then(() => {
        try { silent.pause(); silent.currentTime = 0; silent.volume = 1; } catch {/* ignore */}
        _audioUnlocked = true;
      }).catch(() => {/* ignore — second click will retry */});
    } else {
      _audioUnlocked = true;
    }
  } catch {/* ignore */}

  // Try to prime the Web Speech API too — best-effort.
  if (isTTSSupported()) {
    try {
      loadVoices();
      if (!_voicesLoaded && typeof window.speechSynthesis.onvoiceschanged !== "undefined") {
        window.speechSynthesis.onvoiceschanged = loadVoices;
      }
      const primer = new SpeechSynthesisUtterance(" ");
      primer.volume = 0.01;
      window.speechSynthesis.speak(primer);
      if (_keepAliveInterval) clearInterval(_keepAliveInterval);
      _keepAliveInterval = setInterval(() => {
        try {
          if (!window.speechSynthesis.speaking) window.speechSynthesis.resume();
        } catch {/* ignore */}
      }, 8000);
    } catch (err) {
      _lastError = err?.message || String(err);
    }
  }

  _primed = true;
  return true;
}

// Speak an announcement. The caller passes:
//   text         — full sentence used by SpeechSynthesis
//   tokenNumber  — number used by the MP3 fallback (e.g. "25")
// If SpeechSynthesis works, we use it. Otherwise we fall back to MP3 clips.
export function speakAnnouncement(text, opts = {}) {
  const tokenNumber = opts.tokenNumber ?? extractTokenFromText(text);

  // Already verified fail → straight to MP3.
  if (_speechWorks === false) {
    return playMp3AndReport(tokenNumber);
  }

  if (!isTTSSupported()) {
    _speechWorks = false;
    return playMp3AndReport(tokenNumber);
  }

  if (!_voicesLoaded) loadVoices();

  try {
    try { window.speechSynthesis.cancel(); } catch {/* ignore */}
    setTimeout(() => {
      const u = new SpeechSynthesisUtterance(text);
      u.rate = opts.rate ?? 0.92;
      u.pitch = opts.pitch ?? 1.0;
      u.volume = 1.0;
      u.lang = opts.lang || _preferredLang;
      const voice = pickVoice();
      if (voice) { u.voice = voice; u.lang = voice.lang; }

      let started = false;
      u.onstart = () => { started = true; _speechWorks = true; };
      u.onerror = (e) => {
        _lastError = `utterance error: ${e?.error || "unknown"}`;
        if (!started) {
          _speechWorks = false;
          playMp3Announcement(tokenNumber);
        }
      };
      try { window.speechSynthesis.speak(u); } catch (err) {
        _lastError = err.message;
        _speechWorks = false;
        playMp3Announcement(tokenNumber);
        return;
      }
      // Nudge resume() right after speak() — Chrome bug workaround.
      setTimeout(() => { try { window.speechSynthesis.resume(); } catch {/* ignore */} }, 100);

      // Verdict watchdog: if speak() never fired onstart within 1.2s, treat as
      // silent failure (Google TV / Android Webview pattern) and use MP3.
      setTimeout(() => {
        const speaking = window.speechSynthesis.speaking || started;
        if (!speaking && _speechWorks !== true) {
          _speechWorks = false;
          playMp3Announcement(tokenNumber);
        }
      }, 1200);
    }, 60);
    return true;
  } catch (err) {
    _lastError = err?.message || String(err);
    _speechWorks = false;
    return playMp3AndReport(tokenNumber);
  }
}

function playMp3AndReport(tokenNumber) {
  playMp3Announcement(tokenNumber);
  return true;
}

// Heuristic to pull a number out of "Token number 2 5, John, please proceed…"
function extractTokenFromText(text) {
  if (!text) return "";
  // Match the digits that come after "token number"
  const m = String(text)
    .toLowerCase()
    .match(/token\s+number\s+([\d\s]+)/);
  if (!m) return "";
  return m[1].replace(/\s+/g, "");
}

// Used by the TV Test Voice button. Returns a Promise that resolves to one of:
//   { ok: true, channel: 'speech' | 'mp3' }
//   { ok: false }
export function testTTS(tokenNumberForFallback = "1") {
  return new Promise((resolve) => {
    if (!isTTSSupported()) {
      // Straight to MP3
      playMp3Announcement(tokenNumberForFallback).then((ok) => {
        if (ok) _speechWorks = false;
        resolve({ ok, channel: "mp3" });
      });
      return;
    }
    try { window.speechSynthesis.cancel(); } catch {/* ignore */}
    let started = false;
    const u = new SpeechSynthesisUtterance(
      `Voice test, Token number ${tokenNumberForFallback}, please proceed to Counter 1.`
    );
    u.volume = 1.0; u.rate = 0.92; u.pitch = 1.0;
    const voice = pickVoice();
    if (voice) { u.voice = voice; u.lang = voice.lang; }
    u.onstart = () => { started = true; _speechWorks = true; };
    setTimeout(() => {
      try { window.speechSynthesis.speak(u); } catch {/* ignore */}
    }, 80);
    setTimeout(async () => {
      const speaking = window.speechSynthesis.speaking || started;
      if (speaking) {
        resolve({ ok: true, channel: "speech" });
      } else {
        _speechWorks = false;
        const mp3Ok = await playMp3Announcement(tokenNumberForFallback);
        resolve({ ok: mp3Ok, channel: "mp3" });
      }
    }, 1500);
  });
}

export function getTTSStatus() {
  if (!_voicesLoaded) loadVoices();
  let channel;
  if (_speechWorks === true) channel = "speech";
  else if (_speechWorks === false) channel = "mp3";
  else channel = "auto";
  return {
    supported: isTTSSupported(),
    primed: _primed,
    voicesLoaded: _voicesLoaded,
    voiceCount: _voices.length,
    selectedVoice: pickVoice()?.name || null,
    speechWorks: _speechWorks,
    audioUnlocked: _audioUnlocked,
    channel,
    lastError: _lastError,
  };
}

// Audio "ding" using Web Audio API — same as before.
export function playDing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.18);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.45, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.7);
    o.start(); o.stop(ctx.currentTime + 0.75);
  } catch (err) { console.warn("[AIBox] ding failed:", err); }
}
